/**
 * ProfileCardTrigger — wraps any avatar/name; on tap opens the ProfileCard in a
 * portal overlay without navigating away from the surface (Phase A, AC-02.1).
 *
 * Gated by `social.profile_card`: when the flag is off it renders its children
 * inertly (the surface keeps its prior inline avatar/name behaviour).
 */

import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { ProfileCard } from './ProfileCard';
import type { ProfileCard as ProfileCardModel } from '../../types/social';
import './ProfileCardTrigger.css';

interface Props {
  profileId: string;
  children: ReactNode;
  onMessage?: (connectionId: string, card: ProfileCardModel) => void;
  className?: string;
}

export function ProfileCardTrigger({ profileId, children, onMessage, className }: Props) {
  const enabled = useFeatureFlag('social.profile_card');
  const [open, setOpen] = useState(false);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        className={`profile-card-trigger ${className ?? ''}`}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          // Stop the open from also triggering a parent row/header onClick
          // (e.g. conversation-row select, swipe-card detail toggle).
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }
        }}
      >
        {children}
      </span>

      {open &&
        createPortal(
          <div
            className="profile-card-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <ProfileCard
              profileId={profileId}
              onClose={() => setOpen(false)}
              onMessage={(connectionId, card) => {
                setOpen(false);
                onMessage?.(connectionId, card);
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
}

export default ProfileCardTrigger;
